// R217 — **THE SEAL ROOM, AND THE TWO STATES IT COULD NOT TELL APART.**
//
// This room is the OTHER of the only two on this site that render `GenerationQueueStrip`, and the
// comment it renders that strip under names the case in as many words: *"they returned mid-run, or
// started one and wandered."* **It asked the server nothing.** Its Generate button's label and its
// disable both read a local `busy` that dies on navigation — so on the very return R19 exists for,
// the strip pulsed *Your seal · still working* while the button one line above it read **Create My
// Seal** and was fully pressable. R19 repaired that in the astrocartography room's LABEL, R215
// repaired it in the same room's DISABLE, and nobody ever asked whether the room next door had the
// same hole. It had both, on **the most expensive single generation in this house.**
//
// **AND THE FIVE-MINUTE POLL IS THE STATE THE ROUND IS NAMED FOR.** `pollSeal` gives up at its
// deadline with `retryable: true` and *"taking longer than usual … one tap runs it fresh"*, and its
// own comment says why that is safe: *"Out of time, not out of luck: the run is detached."* The room
// answered it with the same **Run it again** it offers a seal that genuinely died — so the house's
// own sentence invited a SECOND PAID RENDER of a seal that was still being drawn. *The poll gave up*
// and *the seal gave up* are two different facts (R18) and this room had one sentence and one control
// for both. `sealRunState` in `src/generations.js` is the law, so a gate RUNS it; the resume reads
// its job key off the poll payload the house already sends, so nothing new goes on the wire.
//
// **WHY IT IS OUT HERE AT ALL:** roadmap 7's own currency (R8's ledger line — *the thing that had to
// move is the thing nothing could reach*). Window 10 measured this component as the densest remaining
// in App.tsx and REFUSED to extract it, because it was clean and R19 forbids a change with no defect
// under it — *"look for the DEFECT first."* This is that defect, and the move is what pays the
// ceiling the repair spends.
//
// It holds hooks, so it is the stateful class `TierCards` and `SignInModal` already moved as: what
// the extraction buys is the IMPORT, not a gate that can render it.
import { useState, type FormEvent } from "react";
import { ArrowLeft } from "lucide-react";
import { t } from "../strings.js";
import {
  SEAL_LANGUAGES,
  ringStringsFor,
  SEAL_MAX_NAMES,
  SEAL_MAX_AFFIRMATIONS,
  SEAL_MAX_RING_STRINGS,
  affTooLongArgs,
  affirmationNote,
  splitSealLines,
  dedupeSealNameLines,
} from "../magicRoom.js";
import { sealRunState } from "../generations.js";
import { consumeSealNames } from "./AngelsDoor";
import {
  startSeal,
  pollSeal,
  sealParagraphs,
  sealHasMeaning,
  type GeneratedSeal,
  type SealName,
} from "../lib/seal-run";
import { type LockInfo } from "../lib/tier-catalog";
import { useActiveGenerations, GenerationQueueStrip } from "./GenerationStrips";
import { CardZoomOverlay } from "./TarotViews";

export function SealCreatorPage({ isMember, onRequireMembership, goHome, language }: { isMember: boolean; onRequireMembership: (action: () => void, lock?: LockInfo) => void; goHome: () => void; language: string }) {
  // S6: "What is this seal for?" is gone; the evaluator derives the purpose from the boxes. A
  // "Symbols to include" box is added, and it is the ONLY source of symbols on the seal.
  // >>> IT #146 — HER ANGELS ARRIVE IN THE BOX. <<< Read at mount through a LAZY INITIALISER
  // rather than an effect, because an effect runs after the paint and she would watch an empty
  // form fill itself. Reading does NOT clear the handoff — its own deadline does — which is what
  // makes this correct under StrictMode, where React double-invokes this initialiser: a
  // consume-clears reader would hand the second call nothing and lose her angels (BREACH-1's
  // ruling about a guard that must survive StrictMode, one surface over).
  const [sealRequest, setSealRequest] = useState<{ names: string; languages: string[]; affirmations: string; symbols: string; style: string }>(() => ({
    names: consumeSealNames().join("\n"),
    languages: ["English"],
    affirmations: "",
    symbols: "",
    style: "",
  }));
  const names = dedupeSealNameLines(splitSealLines(sealRequest.names));
  const affirmations = splitSealLines(sealRequest.affirmations);
  const droppedDuplicates = splitSealLines(sealRequest.names).length - names.length;
  const overNames = names.length > SEAL_MAX_NAMES;
  const overAffirmations = affirmations.length > SEAL_MAX_AFFIRMATIONS;
  // R15: the field used to accept what the server refuses. Both the guard and the note are the law's.
  const affTooLong = affTooLongArgs(affirmations);
  const affNote = affirmationNote(affirmations);
  const ringStrings = ringStringsFor(names.length, sealRequest.languages);
  const perName = names.length ? ringStrings / names.length : 1;
  const namesAllowed = Math.floor(SEAL_MAX_RING_STRINGS / perName);
  const ringTooFull = !overNames && ringStrings > SEAL_MAX_RING_STRINGS;
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [retryable, setRetryable] = useState(false); // SF3: a failed-but-rerunnable seal shows [Run it again]
  const [seal, setSeal] = useState<GeneratedSeal | null>(null);
  // R63: the names the server returns WITH a refusal, which the room never rendered.
  const [failedNames, setFailedNames] = useState<SealName[]>([]);
  const [zoom, setZoom] = useState(false);
  const [edit, setEdit] = useState("");

  // R217 — WHAT THE SERVER KNOWS, WHICH THIS ROOM HAD NEVER ASKED. The strip below already polls
  // this exact list; reading it here is the same poll and no second one (R49/R28/R29).
  const { active: activeGenerations } = useActiveGenerations(true);
  const { working, resumable, jobKey: liveJobKey } = sealRunState({ busy, active: activeGenerations });

  function toggleLanguage(lang: string) {
    setSealRequest((current) => {
      const has = current.languages.includes(lang);
      const languages = has ? current.languages.filter((l) => l !== lang) : [...current.languages, lang];
      return { ...current, languages: languages.length ? languages : [lang] }; // never empty
    });
  }

  function prepareSeal(event: FormEvent) {
    event.preventDefault();
    // S6: no "what is this for" gate. A seal needs at least a name or an affirmation to inscribe.
    if (!names.length && !affirmations.length) { setStatus(t(language, "seal.needSomething")); return; }
    // S2.5: caught BEFORE she spends a generation, and told as a real number against a real
    // limit. The server says the same thing to any caller; this is so she never has to hear it
    // from the server at all.
    if (overNames) { setStatus(t(language, "seal.overNames", { max: SEAL_MAX_NAMES, count: names.length })); return; }
    if (ringTooFull) { setStatus(t(language, "seal.ringTooFull", { strings: ringStrings, allowed: namesAllowed })); return; }
    if (overAffirmations) { setStatus(t(language, "seal.overAffirmations", { max: SEAL_MAX_AFFIRMATIONS, count: affirmations.length })); return; }
    if (affTooLong) { setStatus(t(language, "seal.affTooLong", affTooLong)); return; }
    onRequireMembership(() => { void runGenerate(); });
  }

  // Q4f: regenerate the seal with the member's stated change ("thicker ring, remove the moon")
  // fed in, keeping every other detail. The same generate path, with an `edit` instruction.
  function submitEdit() {
    if (!edit.trim()) return;
    onRequireMembership(() => { void runGenerate(edit.trim()); });
  }

  // R63: the whole run — the start, the five-minute poll and what every answer MEANS — is
  // `src/lib/seal-run.ts`, where a gate RUNS it. The loop used to live inside one big `try`, so a
  // single dropped poll threw straight past R18's own transport-noise law and told her the seal was
  // lost while the detached pipeline was still drawing it.
  // R217 lifted these three to component scope, unchanged, so the RESUME reaches the same words and
  // the same two answers rather than composing a second set of its own (R28/R29).
  const copy = { lost: t(language, "seal.lost"), slow: t(language, "seal.slow") };
  const deliver = (s: GeneratedSeal) => { setSeal(s); setStatus(""); setEdit(""); setFailedNames([]); };
  // ON A FAILURE THE SERVER SENDS HER NAMES AND THEY USED TO REACH NOBODY. There is no
  // description on this path, so this list is the only thing telling her the resolver's work —
  // the one paid call on this route — is done and saved.
  const refuse = (o: { message: string; retryable: boolean; names: SealName[] }) => {
    setStatus(o.message || copy.lost);
    setRetryable(o.retryable);
    setFailedNames(o.names);
  };
  // S1-A: a seal status describes PROGRESS ONLY. It never names a colour or a style, because a
  // member reads "in gold on white" as the seal being made in colours they did not choose. The
  // machinery-naming ban applies here too.
  function startStages(first: string) {
    const stages = [
      t(language, "seal.stage.preparing"),
      t(language, "seal.stage.names"),
      t(language, "seal.stage.drawing"),
      t(language, "seal.stage.inscribing"),
      t(language, "seal.stage.checking"),
    ];
    let i = 0;
    setStatus(first || stages[0]);
    return window.setInterval(() => { i = (i + 1) % stages.length; setStatus(stages[i]); }, 3000);
  }

  // R217 — **PICKING THE WAIT BACK UP, WHICH COSTS NOTHING.** `pollSeal` gives up at five minutes
  // and says so; the pipeline it was watching does not. This re-enters the wait on the job the
  // server still has, and it POSTS NOTHING — no `startSeal`, no second render, no charge. The key is
  // the one already on the poll payload, so this survives a navigation as well as a timeout.
  async function resumeSeal(key: string) {
    if (!key) return;
    setBusy(true);
    setRetryable(false);
    setFailedNames([]);
    const timer = startStages("");
    try {
      const outcome = await pollSeal(key, copy, { defaultTitle: t(language, "seal.defaultTitle") });
      if (outcome.ok) deliver(outcome.seal); else refuse(outcome);
    } finally {
      window.clearInterval(timer);
      setBusy(false);
    }
  }

  async function runGenerate(editInstruction?: string) {
    setBusy(true);
    setSeal(null);
    setRetryable(false);
    setFailedNames([]);
    const timer = startStages(editInstruction ? t(language, "seal.reworkingStatus") : "");
    try {
      // S2.2 — THE STRUCTURED SUBMISSION ON THE WIRE. names[] and affirmations[] as arrays, her
      // words verbatim, so the server has her actual list to verify the seal against.
      const started = await startSeal({
        names, affirmations,
        languages: sealRequest.languages,
        symbols: sealRequest.symbols,
        style: sealRequest.style,
        edit: editInstruction || "",
      }, fetch, t(language, "seal.defaultTitle"));
      if (started.ok && started.kind === "done") { deliver(started.seal); return; }
      if (!started.ok) {
        if (started.kind === "sign-in") { setStatus(t(language, "seal.signIn")); return; }
        // SF3: an honest single message + a one-tap rerun (the form state is still here, so nothing
        // is retyped). No "being reworked" contradiction, no machinery talk.
        if (started.kind === "offline") { refuse({ message: copy.lost, retryable: true, names: [] }); return; }
        refuse(started);
        return;
      }
      // SF2: the seal generates DETACHED, so the request can never be held open long enough for the
      // platform to time it out. The poll is what delivers it.
      const outcome = await pollSeal(started.jobKey, copy, { defaultTitle: t(language, "seal.defaultTitle") });
      if (outcome.ok) deliver(outcome.seal); else refuse(outcome);
    } finally {
      window.clearInterval(timer);
      setBusy(false);
    }
  }

  const pdfHref = (kind: "image" | "description") =>
    seal?.sealId ? `/api/seals/generated/${seal.sealId}/pdf/${kind}` : undefined;

  return (
    <section className="page-shell specialty-page seals-page">
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        {t(language, "common.backHome")}
      </button>

      <div className="seal-room">
        <div className="specialty-hero">
          <p className="eyebrow">Create Your Own Seals</p>
          <h1>{t(language, "seal.title")}</h1>
          <p>{t(language, "seal.lede")}</p>
        </div>

        <form className="seal-form" onSubmit={prepareSeal}>
          <label className="wide">
            <span>{t(language, "seal.names.label")}</span>
            <textarea
              className="seal-form__lines"
              value={sealRequest.names}
              onChange={(event) => setSealRequest((current) => ({ ...current, names: event.target.value }))}
              placeholder={"Yeyayel\nPoiel\nEl Shaddai"}
              rows={4}
            />
            <span className={`seal-form__count${overNames || ringTooFull ? " seal-form__count--over" : ""}`}>
              {/* Over the line, the count must say what to DO about it. "13 of 12 names, each
                  inscribed once" states the limit and the reassurance in the same breath and
                  reads like nothing is wrong. */}
              {overNames
                ? t(language, "seal.names.over", { count: names.length, max: SEAL_MAX_NAMES, extra: names.length - SEAL_MAX_NAMES })
                : ringTooFull
                  // The measured trade, said plainly while she can still change her mind.
                  ? t(language, "seal.names.ringFull", { count: names.length, scripts: sealRequest.languages.length, strings: ringStrings, max: SEAL_MAX_RING_STRINGS, allowed: namesAllowed })
                  : names.length === 0
                    ? t(language, "seal.names.empty", { max: SEAL_MAX_NAMES })
                    /* X4: no running counter while the entry is valid — capacity surfaces only as
                       honest validation (the over/ring-too-full branches) and at submit. */
                    : ""}
              {/* OWNER RULING 1: she is TOLD it collapsed. Silently dropping a line she typed
                  would be its own small lie, and the last seal's description inventing a reason
                  for her duplicate is exactly what this whole item is answering. */}
              {droppedDuplicates > 0 ? ` ${t(language, droppedDuplicates > 1 ? "seal.names.collapsedMany" : "seal.names.collapsedOne", { count: droppedDuplicates })}` : ""}
            </span>
          </label>
          <div className="seal-langs">
            <span className="seal-langs__label">{t(language, "seal.script.label")}</span>
            <div className="seal-langs__options">
              {SEAL_LANGUAGES.map(({ label }) => (
                <button
                  key={label}
                  type="button"
                  className={sealRequest.languages.includes(label) ? "seal-lang seal-lang--on" : "seal-lang"}
                  aria-pressed={sealRequest.languages.includes(label)}
                  onClick={() => toggleLanguage(label)}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* IT #294: the picker binds the SACRED NAMES; her own words are inscribed verbatim
                (owner ruling, seal-submission.js). A member who chose Hebrew expected her
                affirmation translated, so the form now says what each part does. */}
            <span className="seal-langs__hint">{t(language, "seal.script.hint")}</span>
          </div>
          {/* S2.2 — captured verbatim, quoted back verbatim, and (S4) inscribed by the scribe. */}
          <label className="wide">
            <span>{t(language, "seal.aff.label")}</span>
            <textarea
              className="seal-form__lines"
              value={sealRequest.affirmations}
              onChange={(event) => setSealRequest((current) => ({ ...current, affirmations: event.target.value }))}
              placeholder={t(language, "seal.aff.placeholder")}
              rows={4}
            />
            <span className={`seal-form__count${affNote?.over ? " seal-form__count--over" : ""}`}>
              {affNote ? t(language, affNote.key, affNote.args) : ""}
            </span>
          </label>
          {/* S6: symbols are optional and are the ONLY source of symbolism on the seal. Empty box
              means a pure-design seal (geometry and ornament, no symbols). */}
          <label className="wide">
            <span>{t(language, "seal.symbols.label")}</span>
            <input
              value={sealRequest.symbols}
              onChange={(event) => setSealRequest((current) => ({ ...current, symbols: event.target.value }))}
              placeholder={t(language, "seal.symbols.placeholder")}
            />
          </label>
          <label className="wide">
            <span>{t(language, "seal.style.label")}</span>
            <input
              value={sealRequest.style}
              onChange={(event) => setSealRequest((current) => ({ ...current, style: event.target.value }))}
              placeholder={t(language, "seal.style.placeholder")}
            />
          </label>
          {/* R217 — THE DOOR READS THE SERVER NOW. `busy` dies on navigation, so this button used to
              read "Create My Seal" and be fully pressable on the very return the strip below it
              exists for, and the tap bought a SECOND paid render of a seal already being drawn.
              `working` is local OR the server's answer, and it decides both halves — the pulse (R19)
              and the disable (R215) — because a room whose door LOOKS shut and is not is the worse
              of the two. Over-closing costs her a wait the strip is already showing her; under-
              closing costs a paid seal she cannot see (R19's direction, R8-4's silent one). */}
          <button className={`button button--primary${working ? " is-generating" : ""}`} type="submit" disabled={working}>
            {t(language, working ? "seal.going" : "seal.go")}
          </button>
          {/* R217 — TWO FACTS, AND THE ROOM HAD ONE SENTENCE FOR BOTH (R18). `seal.slow` ends on
              "one tap runs it fresh", which is the one thing that must not happen while the pipeline
              is still drawing. The substitution is scoped to the state that is wrong — the server
              still has her seal AND the room is offering a re-run — so a seal that genuinely died
              keeps its own words, untouched. */}
          {status ? <p className="seal-status">{resumable && retryable ? t(language, "seal.stillDrawing") : status}</p> : null}
          {/* R63: the server puts her resolved names on every refusal and says why in its own file
              ("the client names them while the artwork is perfected"). The client dropped them. On a
              failure there is no description, so this list is the only place they appear. */}
          {failedNames.length ? (
            <p className="seal-status seal-status--names">
              {t(language, "seal.namesHeld", { names: failedNames.map((n) => n.roman || n.native || "").filter(Boolean).join(", ") })}
            </p>
          ) : null}
          {/* R217 — WHICH TAP SHE IS OFFERED IS DECIDED BY WHETHER THE SEAL IS STILL BEING DRAWN.
              It is the same control in both states and never two controls on screen at once: the
              resume POSTS NOTHING and re-enters the wait on the job the server still holds, and the
              re-run is the one that starts a render. `resumable` wins, because a re-run offered over
              a live pipeline is the second paid seal this whole round is about — and the server's
              own dedupe cannot catch it, being keyed on the CONTENT of a form she has just retyped
              or adjusted. */}
          {resumable ? (
            <button className="button button--primary" type="button" onClick={() => { void resumeSeal(liveJobKey); }}>
              {t(language, "seal.keepWaiting")}
            </button>
          ) : retryable && !working ? (
            <button className="button button--primary" type="button" onClick={() => { void runGenerate(); }}>
              {t(language, "seal.runAgain")}
            </button>
          ) : null}
        </form>

        {/* G3: the in-room queue. If a seal is still working (they returned mid-run, or started
            one and wandered), it shows here, pulsing, and flips to a ready entry that opens it. */}
        <GenerationQueueStrip
          room="seals" language={language}
          onOpen={(savedId) => { if (savedId) window.open(`/api/seals/generated/${savedId}/image`, "_blank"); }}
        />

        {seal ? (
          <div className="seal-reveal">
            {/* The seal itself: image alone, clean, with its own PDF. */}
            <div className="seal-reveal__art">
              <h2 className="seal-reveal__title">{seal.title}</h2>
              <button type="button" className="seal-reveal__imgbtn" onClick={() => setZoom(true)} aria-label={t(language, "seal.zoom")}>
                <img className="seal-reveal__img" src={seal.imageUrl} alt={seal.title} />
              </button>
              {pdfHref("image") ? (
                <a className="button button--ghost" href={pdfHref("image")} download>{t(language, "seal.downloadImage")}</a>
              ) : null}
              {/* Q4f: state a change and regenerate the seal with it applied, through all gates. */}
              <div className="seal-edit">
                <label className="seal-edit__label" htmlFor="seal-edit-input">{t(language, "seal.edit.label")}</label>
                <div className="seal-edit__row">
                  <input
                    id="seal-edit-input"
                    value={edit}
                    onChange={(event) => setEdit(event.target.value)}
                    placeholder={t(language, "seal.edit.placeholder")}
                    onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); submitEdit(); } }}
                    disabled={working}
                  />
                  {/* R217: a rework IS a render, so it closes on the same answer the start door
                      does. With nothing in flight `working` IS `busy`, so this is today's behaviour
                      exactly everywhere except the state the round is about. */}
                  <button type="button" className="button button--ghost" onClick={submitEdit} disabled={working || !edit.trim()}>
                    {t(language, working ? "seal.edit.going" : "seal.edit.go")}
                  </button>
                </div>
              </div>
            </div>

            {/* Separate box: the full description so the member understands their seal.
                R63 — T9-2's NO BODY, NO BOX, one room over. `"".split(/\n{2,}/)` is `[""]`, so a
                delivered seal carrying no description drew "What your seal means" over one empty
                paragraph: W12-5's DATE-OF-BIRTH-over-nothing on the most expensive surface here.
                The heading is DERIVED from there being something under it. */}
            {sealHasMeaning(seal) ? (
            <div className="seal-reveal__meaning">
              <h3>{t(language, "seal.meaning")}</h3>
              {seal.names.length ? (
                <ul className="seal-reveal__names">
                  {seal.names.map((n, idx) => {
                    // Q4g: show every chosen-language form the seal carries.
                    const forms = (n.forms && n.forms.length ? n.forms.map((f) => f.text || "") : [n.native || ""])
                      .map((t) => (t || "").trim())
                      .filter((t) => t && t !== (n.roman || ""));
                    return (
                      <li key={idx}>
                        <span className="seal-reveal__name-word">{n.roman || n.native}</span>
                        {forms.map((t, j) => (
                          <span key={j} className="seal-reveal__name-native">{t}</span>
                        ))}
                        {n.meaning ? <span className="seal-reveal__name-meaning">{n.meaning}</span> : null}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              {/* **THE FLAG SHE CAN SEE (her order, 2026-08-08).** Stage 5 marks a seal
                  `delivered-with-flag` when the read-back could not confirm every inscription, and
                  that mark went to the trail and NOWHERE ELSE — she received a seal reading
                  WOUND.AITH and HEALLTH and was told nothing. It sits ABOVE the description, because
                  a member reads the words under her seal and this is the one line that changes what
                  she might do next. Empty on a clean seal, so a good one says nothing at all. */}
              {seal.flagNote ? <p className="seal-reveal__flag" role="status">{seal.flagNote}</p> : null}
              {sealParagraphs(seal.description).map((para, idx) => (
                <p key={idx} className="seal-reveal__para">{para}</p>
              ))}
              {pdfHref("description") ? (
                <a className="button button--ghost" href={pdfHref("description")} download>{t(language, "seal.downloadDescription")}</a>
              ) : null}
              {seal.sealId ? <p className="seal-reveal__saved">{t(language, "seal.savedNote")}</p> : null}
            </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {zoom && seal ? (
        <CardZoomOverlay src={seal.imageUrl} alt={seal.title} onClose={() => setZoom(false)} />
      ) : null}

    </section>
  );
}
